import { CircularProgress, Stack, Typography } from "@mui/material"
import { CommentItem } from "../CommentItem"
import type { OpenL2ReplyThreadPayload } from "../ReplyItem"
import { useCallback, useEffect, useState } from "react"
import { useFormContext } from "react-hook-form"
import { EmptyListState } from "../../../ui-kit/EmptyListState"
import { CommentsType } from "../../../../types/common"
import type { CommentsSortUi, TGetCommentsResponse } from "../../../../api/types/projects"
import { mapApiCommentsToUiComments } from "../../helpers/commentDataMappers"
import type { CommentEditState } from "../CommentEditableText"

type CommentsListProps = {
    commentsData?: TGetCommentsResponse
    onSubmitReply?: (payload: { text: string; parentId: number }) => Promise<boolean>
    isSubmittingComment?: boolean
    isCommentsLoading?: boolean
    isCommentsError?: boolean
    loadMoreReplies?: (parentId: number) => void | Promise<void>
    loadingParentId?: number | null
    currentUserId?: number | null
    onUpdateComment?: (commentId: number, text: string) => Promise<boolean>
    isUpdatingComment?: boolean
    onDeleteComment?: (commentId: number) => Promise<boolean>
    onOpenReplyThread?: (p: OpenL2ReplyThreadPayload) => void
    commentReactionCache?:
        | { kind?: 'project'; projectId: number; type: string; sort?: CommentsSortUi; threadParentId?: number }
        | { kind: 'growthTool'; growthToolId: number; type: string; sort?: CommentsSortUi; threadParentId?: number }
        | { kind: 'socialOrder'; socialOrderId: number; type: string; sort?: CommentsSortUi; threadParentId?: number }
    highlightCommentId?: number | null
}

export const CommentsList = ({
    commentsData,
    onSubmitReply,
    isSubmittingComment,
    isCommentsLoading,
    isCommentsError,
    loadMoreReplies,
    loadingParentId,
    currentUserId,
    onUpdateComment,
    isUpdatingComment = false,
    onDeleteComment,
    onOpenReplyThread,
    commentReactionCache,
    highlightCommentId,
}: CommentsListProps) => {
    const [activeCommentId, setActiveCommentId] = useState<number | null>(null)
    const [activeReplyTarget, setActiveReplyTarget] = useState<{
        firstName: string
        lastName: string
        parentId: number
    } | null>(null)
    const [editState, setEditState] = useState<CommentEditState>(null)

    const { setValue, watch } = useFormContext()
    const filterType = watch("filterType") as CommentsType
    const sort = watch("sort")

    const cancelEdit = useCallback(() => setEditState(null), [])

    const setDraft = useCallback((str: string) => {
        setEditState((prev) => (prev ? { ...prev, draft: str } : null))
    }, [])

    const handleBeginEdit = useCallback(
        (commentId: number, originalText: string) => {
            setActiveCommentId(null)
            setActiveReplyTarget(null)
            setValue("replyText", "")
            setValue("mobileReplyTarget", null)
            setValue("commentText", "")
            setEditState({ commentId, original: originalText, draft: originalText })
        },
        [setValue]
    )

    const confirmSave = useCallback(async () => {
        if (!editState || !onUpdateComment) return
        const trimmed = editState.draft.trim()
        if (!trimmed) return
        if (trimmed === editState.original.trim()) {
            cancelEdit()
            return
        }
        const ok = await onUpdateComment(editState.commentId, trimmed)
        if (ok) cancelEdit()
    }, [editState, onUpdateComment, cancelEdit])

    useEffect(() => {
        setActiveCommentId(null)
        setActiveReplyTarget(null)
        setValue("replyText", "")
        setValue("mobileReplyTarget", null)
        setValue("commentText", "")
        setEditState(null)
    }, [filterType, sort, setValue])

    if (isCommentsLoading) {
        return (
            <Stack alignItems="center" justifyContent="center" minHeight="100%">
                <CircularProgress size={24} />
            </Stack>
        )
    }

    if (isCommentsError) {
        return (
            <Stack alignItems="center" justifyContent="center" minHeight="100%">
                <Typography variant="black-14-normal">Не удалось загрузить комментарии</Typography>
            </Stack>
        )
    }

    const resolvedComments = commentsData ?? []
    if (!resolvedComments.length) {
        return <EmptyListState message="Пусто" />
    }

    const uiComments = mapApiCommentsToUiComments(resolvedComments)

    return (
        <Stack gap={2}>
            {uiComments.map((comment) => (
                <CommentItem
                    key={comment.id}
                    comment={comment}
                    activeCommentId={activeCommentId}
                    setActiveCommentId={setActiveCommentId}
                    activeReplyTarget={activeReplyTarget}
                    setActiveReplyTarget={setActiveReplyTarget}
                    onSubmitReply={onSubmitReply}
                    isSubmittingComment={isSubmittingComment}
                    loadMoreReplies={loadMoreReplies}
                    loadingParentId={loadingParentId}
                    currentUserId={currentUserId}
                    editState={editState}
                    onBeginEdit={handleBeginEdit}
                    cancelEdit={cancelEdit}
                    setDraft={setDraft}
                    confirmSave={confirmSave}
                    isUpdatingComment={isUpdatingComment}
                    onDeleteComment={onDeleteComment}
                    onOpenReplyThread={onOpenReplyThread}
                    commentReactionCache={commentReactionCache}
                    highlightCommentId={highlightCommentId}
                />
            ))}
        </Stack>
    )
}
